/* Shared task deep-link grammar (Task-Management Phase 1 / T2, plan §1.5).
 *
 * ONE navigation vocabulary for every actionable row that targets a task:
 * My Work rows (this chunk) and — in a serialized follow-on — the notification
 * bell rows. The canonical target is a server-resolved {clientSlug, taskId};
 * the server owns slug→latest-run resolution, so the /me/tasks (and, later,
 * feed) payloads carry the run file already and the client never guesses it.
 *
 * Two halves, mirroring the data/render split the rest of the hub uses:
 *  - buildTaskHref()  — pure: {clientSlug, taskId, runFile} → the client-hub
 *    URL `?load=<run>&task=<id>#s7`. My Work rows and @-mention chips call it.
 *  - openTaskDeepLink() — the CONSUMER, run on the client hub after the board
 *    loads (main.ts boot). Reads `?task=<id>`: found → scroll + flash-highlight
 *    (+ open the list row's expander; a kanban `.kcard` just flashes, no
 *    expander); stale → a toast (never a dead scroll); no `?task` but `#notes`
 *    → focus the notes card (the client-note-mention landing the bell
 *    fix-forward will reuse).
 *
 * Deliberately NOT wired into ws/notifBell.ts — that bell → helper + flood
 * control is a separate serialized chunk (plan §1.5, program-status #4). This
 * module only provides the shared grammar and makes My Work rows consume it.
 */
import { openTaskPanel } from './taskDetail.ts';
import type { Workspace } from '../state/workspace.ts';
import type { WsTask } from '../types/workspace.ts';

export interface TaskLinkTarget {
  clientSlug: string;
  taskId: number | string;
  /** the client's latest run file (server-resolved, e.g. from /clients
   * lastRunFile). Absent only for a client that has never been run — where a
   * task can't exist anyway; the builder still stays total. */
  runFile?: string | null;
}

/** Build the client-hub deep link for a task: `?load=<run>&task=<id>#s7`
 * (cross-client safe — a My Work row for another client routes to THAT client
 * first via its own run file, then opens the task). With no run file it
 * degrades to the hub's bare entry rather than throwing. */
export function buildTaskHref(t: TaskLinkTarget): string {
  const base = t.runFile
    ? `/review/?load=${encodeURIComponent('/test-runs/' + t.runFile)}`
    : '/review/?x=1';
  return `${base}&task=${encodeURIComponent(String(t.taskId))}#s7`;
}

/** Resolve a `?task=<id>` against the loaded board. Returns the task, or null
 * when the id is no longer in the latest run (a stale deep link). Pure — both
 * the DOM consumer below and the unit tests call it. */
export function resolveTaskTarget(tasks: WsTask[], taskId: number): WsTask | null {
  return tasks.find((t) => Number(t.id) === taskId) ?? null;
}

export const TASK_STALE_MSG = 'That task is no longer in the latest run.';

/** Consume a `?task=<id>` deep link on the CLIENT HUB. Bounded-retry because
 * the board fetch (WS.init) resolves async and paints after this first runs:
 * we wait for `ws.available` to settle (null → true/false) and for the row to
 * paint, then reveal it; if the id isn't in the resolved board it's a stale
 * link → toast. `schedule` is injectable so the unit tests can drive the retry
 * loop synchronously (default: window.setTimeout). */
export function openTaskDeepLink(
  ws: Workspace,
  opts: {
    attempts?: number;
    delayMs?: number;
    schedule?: (fn: () => void, ms: number) => void;
    /** the task-detail opener — defaults to ws/taskDetail.ts's openTaskPanel;
     * injectable so the deep-link unit tests can assert the reveal mechanics
     * (scroll/flash/stale-toast) without standing up the whole panel + its
     * heavy ws deps (same injection convention as `schedule`). */
    open?: (ws: Workspace, task: WsTask) => void;
  } = {},
): void {
  const raw = new URLSearchParams(location.search).get('task');
  if (!raw) {
    if (location.hash === '#notes') focusNotesCard();
    return;
  }
  const taskId = Number(raw);
  if (!Number.isFinite(taskId)) return;
  const schedule = opts.schedule ?? ((fn, ms) => window.setTimeout(fn, ms));
  const open = opts.open ?? openTaskPanel;
  const delay = opts.delayMs ?? 60;
  let left = opts.attempts ?? 40;

  const tick = (): void => {
    // wait until the board fetch has resolved (available: null → true|false)
    if (ws.available === null && left-- > 0) {
      schedule(tick, delay);
      return;
    }
    const task = resolveTaskTarget(ws.tasks, taskId);
    if (!task) {
      ws.toast(TASK_STALE_MSG, 'warn');
      return;
    }
    const row = findRow(taskId);
    if (!row && left-- > 0) {
      schedule(tick, delay); // board resolved but still painting
      return;
    }
    if (!row) {
      ws.toast(TASK_STALE_MSG, 'warn');
      return;
    }
    revealRow(ws, row, task, open);
  };
  tick();
}

function findRow(taskId: number): HTMLElement | null {
  // Board (kanban) view has no `.wsrow` — the task lives on a `.kcard`. Match
  // BOTH so a `?task=` deep link resolves in either view (Blocker 1: targeting
  // `.wsrow` only false-toasted "stale" while the card was on screen).
  return document.querySelector<HTMLElement>(
    `.wsrow[data-tid="${taskId}"], .kcard[data-tid="${taskId}"]`,
  );
}

function revealRow(
  ws: Workspace, row: HTMLElement, task: WsTask, open: (ws: Workspace, task: WsTask) => void,
): void {
  row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  row.classList.add('wsrow-flash');
  window.setTimeout(() => row.classList.remove('wsrow-flash'), 2200);
  // The canonical Task Detail surface (S3): a `?task=` deep link — from a My
  // Work row, the notification bell, or any cross-client link — now opens the
  // full task-detail panel, not the legacy inline row expander. Uniform for
  // both a list `.wsrow` and a kanban `.kcard` (the panel is view-independent);
  // the scroll + flash above still land the user on the source row underneath.
  open(ws, task);
}

function focusNotesCard(): void {
  const card = document.getElementById('wsNotes');
  if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
